import { useEffect, useState } from "react";
// Material Dashboard 2 React components
import MDBox from "theme/components/MDBox";
import MDTypography from "theme/components/MDTypography";
import MDInput from "theme/components/MDInput";
import MDButton from "theme/components/MDButton";

// Material Dashboard 2 React example components
import DashboardLayout from "app/LayoutContainers/DashboardLayout";
import DashboardNavbar from "app/Navbars/DashboardNavbar";
import Footer from "theme/Footer";
import axios from "axios.js";

import {
  CircularProgress,
  Backdrop,
  Snackbar,
  Alert,
  Card,
  Icon,
  Dialog,
  DialogContent,
  DialogActions,
  DialogTitle,
} from "@mui/material";
import usePageTitle from "app/pageTitle";

function ReferralCodePage() {
  const title = "My Referral Code";
  usePageTitle(title);

  const [refreshPage, setRefreshPage] = useState(false);
  const [showLoading, setShowLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [errorMsg, setErrMsg] = useState("");
  const [severity, setSeverity] = useState("");
  const handleClose = () => {
    setOpen(false);
    setErrMsg("");
  };

  const [referralCode, setReferralCode] = useState(null);
  const [bonusMsg, setBonusMsg] = useState();

  useEffect(() => {
    const fetchData = async () => {
      setShowLoading(true);
      await axios
        .get(`agents/referral_code.json`)
        .then((result) => {
          setShowLoading(false);
          // console.log(result);
          if (result.data.status === "success") {
            setReferralCode(result.data.data.referral_code);
          } else {
            setErrMsg(result.data.data.message);
            setSeverity("error");
            setOpen(true);
          }
        })
        .catch(() => {
          setShowLoading(false);
          setErrMsg(
            `Unable to complete request. ${
              navigator.onLine ? "" : "Make sure you have an active internet connection"
            }`
          );
          setSeverity("error");
          setOpen(true);
        });
    };

    fetchData();
  }, [refreshPage]);

  useEffect(() => {
    if (referralCode === null) {
      return;
    }
    let msg = "";
    if (referralCode.percentage === true) {
      msg = (
        <span>
          You get paid <b>{(referralCode.bonus * 100).toFixed(2)}%</b> of their first subscription
          amount.
        </span>
      );
    } else {
      msg = (
        <span>
          You get paid{" "}
          <b>
            {referralCode.currency}
            {referralCode.bonus / 100}
          </b>{" "}
          when they make their first subscription payment.
        </span>
      );
    }
    setBonusMsg(msg);
  }, [referralCode]);

  async function copy(stringtext) {
    try {
      await navigator.clipboard.writeText(stringtext);
      setErrMsg("Copied!");
      setSeverity("success");
      setOpen(true);
    } catch (err) {
      console.error("Failed to copy: ", err);
    }
  }

  const [customCode, setCustomCode] = useState("");
  const [openCustomCodeDialog, setOpenCustomCodeDialog] = useState(false);

  async function customizeCode() {
    if (customCode.length < 1) {
      setSeverity("error");
      setErrMsg("Please enter your unique custom referral code.");
      setOpen(true);
      return;
    }

    setOpenCustomCodeDialog(false);
    setShowLoading(true);
    const json = {
      referral_code: customCode,
    };

    await axios
      .post("agents/referral_code/customize.json", json)
      .then((result) => {
        setShowLoading(false);
        if (result.data.status === "success") {
          setSeverity("success");
          setErrMsg(result.data.data.message);
          setOpen(true);
          setRefreshPage(!refreshPage);
        } else {
          setSeverity("error");
          setErrMsg(result.data.data.message);
          setOpen(true);
        }
      })
      .catch(() => {
        setShowLoading(false);
        setSeverity("error");
        setErrMsg(
          `Unable to complete request. ${
            navigator.onLine ? "" : "Make sure you have an active internet connection"
          }`
        );
        setOpen(true);
      });
  }

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <Backdrop
        sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={showLoading}
        onClick={handleClose}
      >
        <CircularProgress color="inherit" />
      </Backdrop>
      <Snackbar
        open={open}
        autoHideDuration={6000}
        onClose={handleClose}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert onClose={handleClose} severity={severity} sx={{ width: "100%" }}>
          {errorMsg}
        </Alert>
      </Snackbar>
      <Dialog open={openCustomCodeDialog} fullWidth maxWidth="sm">
        <DialogTitle>Customize Code</DialogTitle>
        <DialogContent>
          <MDTypography variant="subtitle2" fontSize={16}>
            Enter your unique custom code below
          </MDTypography>
          <MDBox mt={3}>
            <MDInput
              name="referral_code"
              type="text"
              label="Customized Code"
              variant="outlined"
              fullWidth
              onChange={(e) => setCustomCode(e.target.value)}
            />
          </MDBox>
        </DialogContent>
        <DialogActions>
          <MDButton variant="outlined" color="dark" onClick={() => setOpenCustomCodeDialog(false)}>
            Cancel
          </MDButton>
          <MDButton variant="contained" color="primary" onClick={() => customizeCode()}>
            Submit
          </MDButton>
        </DialogActions>
      </Dialog>
      <MDBox pt={1.5} pb={3} mb={3}>
        {referralCode ? (
          <Card sx={{ width: "100%", marginTop: "10px" }}>
            <MDBox mt={1} align="center" mb={3}>
              <MDBox p={2}>
                <MDTypography variant="h6" fontWeight="medium" textTransform="capitalize">
                  Refer & Earn
                </MDTypography>
                <MDTypography variant="subtitle2" fontSize={15} fontWeight="light" gutterBottom>
                  Refer family and friends to join you as a verified Laddar agent. {bonusMsg}
                </MDTypography>
              </MDBox>
              <MDBox mt={2} p={2}>
                <MDTypography variant="h6" fontWeight="light" fontSize={12}>
                  Your Referral Code
                </MDTypography>
                <MDTypography variant="h6" fontWeight="medium" fontSize={32}>
                  {referralCode.code}
                </MDTypography>
                <MDBox mt={1}>
                  <MDButton
                    variant="contained"
                    color="primary"
                    size="small"
                    startIcon={<Icon>copy</Icon>}
                    onClick={() => copy(referralCode.code)}
                  >
                    Copy
                  </MDButton>
                  &nbsp;
                  <MDButton
                    variant="contained"
                    color="warning"
                    size="small"
                    startIcon={<Icon>edit</Icon>}
                    onClick={() => setOpenCustomCodeDialog(true)}
                  >
                    Customize
                  </MDButton>
                </MDBox>
                <MDBox mt={4}>
                  <MDTypography variant="h6" fontWeight="light">
                    {referralCode.link}
                  </MDTypography>
                  <MDBox mt={0.5}>
                    <MDButton
                      variant="outlined"
                      size="small"
                      color="info"
                      iconOnly
                      onClick={() => copy(referralCode.link)}
                    >
                      <Icon>copy</Icon>
                    </MDButton>
                    &nbsp;&nbsp;
                    <a href={referralCode.link} target="_blank" rel="noreferrer">
                      <MDButton variant="outlined" size="small" color="dark" iconOnly>
                        <Icon>send</Icon>
                      </MDButton>
                    </a>
                  </MDBox>
                </MDBox>
              </MDBox>
            </MDBox>
          </Card>
        ) : (
          <> </>
        )}
      </MDBox>
      <Footer />
    </DashboardLayout>
  );
}

export default ReferralCodePage;
